/**
 * 面板刚弹出的一瞬间不接点击。
 *
 * 牌局里的底部面板没有入场位移，是从被点的那个位置「底下」直接冒出来的：
 * 手机和折叠屏上「我的座位」的起立 / 离开房间两行正好压在自己的座位上
 * （360×800 截图：头像落在「起立」上，名牌与「离开房间」几乎重合），
 * 换座 / 买分那一行下面又是下一张面板的按钮。
 * global.css 给按钮设了 `touch-action: manipulation`，双击的两下都会变成 click，
 * React 又会在第一下里就把面板渲染出来 —— 于是双击自己的名牌 = 当场离开房间，
 * 跳过了「面板本身就是第二步」的那次确认（COPY.md「离开房间要确认」）。
 *
 * 做法：面板打开后的 OPEN_TAP_GUARD_MS 内，挂在遮罩上的捕获阶段处理器把 click 截下
 * （含点遮罩关面板那一下），行里的 onClick 根本收不到。人读完面板再点远不止这么快。
 * Esc 与 Tab 走 keydown，不受影响；打开面板后焦点落在面板本身，不会有「连按两下回车」
 * 直接点中某一行的情况。
 */
import { useCallback, useLayoutEffect, useRef } from 'react'
import type { SyntheticEvent } from 'react'

/** 双击两下之间一般在 300ms 以内，再留一点余量 */
export const OPEN_TAP_GUARD_MS = 400

/** 面板在 `openedAt` 打开，`now` 这一下点击是不是还在防误触窗口里（`openedAt` 为 null：还没打开过） */
export function tapTooSoonAfterOpen(openedAt: number | null, now: number): boolean {
  return openedAt !== null && now - openedAt >= 0 && now - openedAt < OPEN_TAP_GUARD_MS
}

/** 截下的那一下：不冒泡到行、也不落到遮罩的「点空白关面板」上 */
export function swallowIfTooSoon(
  openedAt: number | null,
  now: number,
  event: Pick<SyntheticEvent, 'stopPropagation' | 'preventDefault'>,
): boolean {
  if (!tapTooSoonAfterOpen(openedAt, now)) return false
  event.stopPropagation()
  event.preventDefault()
  return true
}

function nowMs(): number {
  return globalThis.performance?.now() ?? Date.now()
}

/**
 * 返回一个挂在面板遮罩 `onClickCapture` 上的处理器。
 * `open` 由 false 变 true 的那一刻开始计时（常驻、按 open 开合的面板也适用）。
 *
 * ★ 用 layout effect：它在提交阶段同步跑，第一下点击打开面板的那次渲染一提交，
 *   计时就已经开始，第二下点击不可能赶在它前面。
 */
export function useOpenTapGuard(open: boolean): (event: SyntheticEvent) => void {
  const openedAt = useRef<number | null>(null)
  useLayoutEffect(() => {
    openedAt.current = open ? nowMs() : null
  }, [open])
  return useCallback((event: SyntheticEvent): void => {
    swallowIfTooSoon(openedAt.current, nowMs(), event)
  }, [])
}
